import React from "react";
import { PanelLoader } from "../../../../../../components/Loader/PanelLoader";
import { Checkbox, SvgIcon } from "@mui/material";
import { TableComponent } from "../../../../../../components/Table/TableComponent";
import { PieChart } from "../../../../../../components/CHARTS/Pie/PieChart";
import "./ConfigurePanelDialog.css";
import { XyChart } from "../../../../../../components/CHARTS/Xy/XyChart";
import { KpiChart } from "../../../../../../components/CHARTS/Kpi/KpiChart";
import loadingPanelGif from "../../../../../../assets/animations/loading-panel-kodo.gif";
import { selectors } from "../../../../../../redux/features/account/accountSlice";
import { useSelector } from "react-redux";

function CircleIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="#fff"
        stroke="#dddddd"
        strokeWidth="2"
      />
    </SvgIcon>
  );
}

function CircleCheckedIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="#00cf88"
        stroke="#00cf88"
        strokeWidth="2"
      />
      <polyline
        points="7.5,13 11,16.5 16.5,9.5"
        fill="none"
        stroke="#fff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgIcon>
  );
}

export function PreviewPredifinedPanel({ chart, onClick }) {
  // Selección desde Redux
  const isSelected = Boolean(
    useSelector(selectors.selectAccountSelectedPanelById(chart.id))
  );
  // Estado visual solo desde el panel normalizado
  const isLoading = chart.status === "loading";
  const isError = chart.status === "error";
  const isDisabled =
    !chart.datasourceId || (chart.datasourceId && !chart.configuration);

  const getNotConfiguredMessage = () => {
    if (chart.datasourceId) return "";
    if (chart.panelType === "Table" && !chart.configuration)
      return "Table not configured";
    return "Panel not configured";
  };

  const panelNotConfigured = !chart.datasourceId
    ? getNotConfiguredMessage()
    : "";

  const getChartConfiguration = () => {
    if (!chart.datasourceId) return null;
    if (chart.panelType === "Table" && chart.configuration) {
      return {
        component: TableComponent,
        props: {
          metadata: chart.configuration.metaData,
          data: chart.configuration.data,
          thresholdValues: chart.configuration.thresholdValues || [],
        },
      };
    }
    if (chart.panelType === "Pie" && chart.configuration) {
      return {
        component: PieChart,
        props: {
          metadata: chart.configuration.metadata.piePanelDef,
          data: chart.configuration.data,
          showLegend: chart.configuration.metadata.piePanelDef.showLegend,
          legendPosition:
            chart.configuration.metadata.piePanelDef.legendPosition,
          showNumeric:
            chart.configuration.metadata.piePanelDef.showNumeric || false,
          showPercentage:
            chart.configuration.metadata.piePanelDef.showPercentage || false,
          innerRadius:
            chart.configuration.metadata.piePanelDef.innerRadius || 0,
          thresholdValues: chart.configuration.metadata.thresholdValues || [],
          isPreview: true,
        },
      };
    }
    if (chart.panelType === "XY Series" && chart.configuration) {
      return {
        component: XyChart,
        props: {
          configuration: chart.configuration,
          isPreview: true,
        },
      };
    }
    if (chart.panelType === "KPI" && chart.configuration) {
      return {
        component: KpiChart,
        props: {
          data: chart.configuration?.data,
          metadata: chart.configuration?.metadata,
          auxComparisonData: chart.configuration?.auxComparisonData,
        },
      };
    }
    return null;
  };

  const chartConfig = getChartConfiguration();

  const tableStyles = {
    height: "8rem",
    maxHeight: "100%",
    position: "relative",
    overflow: "auto",
    display: "block",
    transform: "scale(0.9)",
  };

  const chartStyles = {
    height: "100%",
    maxHeight: "100%",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "scale(0.9)",
  };

  return (
    <div
      className={`panel-container-item${isSelected ? " panel-selected" : ""}${
        isDisabled ? " panel-disabled" : ""
      }${isError ? " panel-error" : ""}`}
      onClick={isDisabled ? undefined : () => onClick(chart)}
      key={chart.id}
    >
      <div className="panel-item-header">
        {!isDisabled && (
          <Checkbox
            className={`custom-round-checkbox${
              isError ? " checkbox-error" : ""
            }`}
            checked={isSelected}
            icon={<CircleIcon />}
            checkedIcon={<CircleCheckedIcon />}
            onChange={isDisabled ? undefined : () => onClick(chart)}
            onClick={(e) => e.stopPropagation()}
            sx={{ padding: 0 }}
            disabled={isDisabled}
          />
        )}
        <span className="title">{chart.title}</span>
        <span className="icon-more-vertical"></span>
      </div>
      <div className="panel-item-body">
        {isLoading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              padding: "1.5rem 0",
            }}
          >
            <img
              src={loadingPanelGif}
              alt="Loading panel"
              style={{
                width: "100px",
                height: "100px",
              }}
            />
            <div
              style={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                color: "#333333",
              }}
            >
              Loading...
            </div>
            <div style={{ fontSize: "0.7rem", color: "#888" }}>
              Preparing your panel data...
            </div>
          </div>
        ) : isError &&
          (!chart.configuration ||
            !chart.configuration.data ||
            chart.configuration.data.length === 0) ? (
          <div className="panel-error-content">
            <div className="panel-error-icon">
              {/* Icono del gráfico correspondiente en rojo */}
              {chart.panelType === "Table" && (
                <SvgIcon style={{ color: "#d32f2f", fontSize: 40 }}>
                  <rect
                    x="4"
                    y="8"
                    width="16"
                    height="8"
                    rx="2"
                    fill="#d32f2f"
                  />
                </SvgIcon>
              )}
              {chart.panelType === "Pie" && (
                <SvgIcon style={{ color: "#d32f2f", fontSize: 40 }}>
                  <circle cx="12" cy="12" r="10" fill="#d32f2f" />
                </SvgIcon>
              )}
              {chart.panelType === "XY Series" && (
                <SvgIcon style={{ color: "#d32f2f", fontSize: 40 }}>
                  <polyline
                    points="4,16 8,8 16,12 20,6"
                    fill="none"
                    stroke="#d32f2f"
                    strokeWidth="2"
                  />
                </SvgIcon>
              )}
              {chart.panelType === "KPI" && (
                <SvgIcon style={{ color: "#d32f2f", fontSize: 40 }}>
                  <text
                    x="12"
                    y="16"
                    textAnchor="middle"
                    fontSize="16"
                    fill="#d32f2f"
                  >
                    KPI
                  </text>
                </SvgIcon>
              )}
            </div>
            <div className="panel-error-text">
              <span style={{ fontWeight: "bold", color: "#d32f2f" }}>
                Error loading panel data
              </span>
              <br />
              <span style={{ fontSize: "0.95rem", color: "#d32f2f" }}>
                Please try again later.
              </span>
            </div>
          </div>
        ) : chartConfig ? (
          <section
            className="panel-item-chart"
            style={chart.panelType === "Table" ? tableStyles : chartStyles}
          >
            {React.createElement(chartConfig.component, chartConfig.props)}
          </section>
        ) : (
          <div
            style={{ color: "#888", textAlign: "center", padding: "1.5rem 0" }}
          >
            {panelNotConfigured}
          </div>
        )}
      </div>
    </div>
  );
}
